import Dexie, { Table } from 'dexie';

// Types for local database entities
export interface LocalUserGoal {
  localId?: number;
  id?: string;
  user_id: string;
  goal_id: string;
  title: string;
  category?: string;
  difficulty?: string;
  icon?: string;
  target_value?: number;
  current_value?: number;
  points_reward?: number;
  xp_reward?: number;
  streak?: number;
  longest_streak?: number;
  total_completions?: number;
  completed?: boolean;
  is_selected?: boolean;
  last_completed_date?: string;
  validation_type?: string;
  metadata?: any;
  organization_id?: string;
  synced_at?: number | null;
  updated_at?: string;
  created_at?: string;
}

export interface LocalUserProgression {
  localId?: number;
  id?: string;
  user_id: string;
  current_experience: number;
  total_experience: number;
  monthly_experience: number; // ✅ NOUVEAU
  experience_to_next_level: number;
  current_level: number;
  current_rank?: string;
  current_tier?: string;
  progression_type?: string;
  current_streak: number;
  longest_streak: number;
  last_activity_at?: string;
  level_up_at?: string;
  last_goals_reset?: string;
  metadata?: any;
  organization_id?: string;
  synced_at?: number | null;
  updated_at?: string;
  created_at?: string;
}

export interface LocalUserAccomplishment {
  localId?: number;
  id?: string;
  user_id: string;
  accomplishment_id: string;
  accomplishment_name: string;
  accomplishment_type: string;
  accomplishment_description?: string;
  accomplishment_icon_url?: string;
  category?: string;
  tier?: string;
  rarity?: string;
  points_value?: number;
  is_featured?: boolean;
  expires_at?: string;
  unlocked_at: string;
  metadata?: any;
  organization_id?: string;
  synced_at?: number | null;
  updated_at?: string;
  created_at?: string;
}

export interface LocalChallengeParticipation {
  localId?: number;
  id?: string;
  user_id: string;
  challenge_id: string;
  challenge_name: string;
  challenge_type: string;
  target_value: number;
  current_value?: number;
  progress_percentage?: number;
  points_earned?: number;
  bonus_points?: number;
  status: string;
  started_at: string;
  completed_at?: string;
  expires_at?: string;
  metadata?: any;
  organization_id?: string;
  department_id?: string;
  team_id?: string;
  synced_at?: number | null;
  updated_at?: string;
  created_at?: string;
}

export interface LocalUserInventory {
  localId?: number;
  id?: string;
  user_id: string;
  reward_id: string;
  item_name: string;
  item_type: string;
  item_category?: string;
  quantity: number;
  status: string;
  product_template_id?: string;
  claim_id?: string;
  unlocked_at: string;
  activated_at?: string;
  expires_at?: string;
  metadata?: any;
  organization_id?: string;
  synced_at?: number | null;
  updated_at?: string;
  created_at?: string;
}

export interface LocalActiveBoost {
  localId?: number;
  id?: string;
  user_id: string;
  inventory_item_id: string;
  boost_name: string;
  boost_type: string;
  multiplier: number;
  duration_minutes: number;
  remaining_time_minutes?: number;
  status: string;
  activated_at: string;
  expires_at: string;
  metadata?: any;
  organization_id?: string;
  synced_at?: number | null;
  updated_at?: string;
  created_at?: string;
}

export interface LocalStreakProtection {
  localId?: number;
  id?: string;
  user_id: string;
  freeze_item_id: string;
  freeze_template_id: string;
  streak_type: string;
  protection_days: number;
  goal_id?: string;
  goal_title?: string;
  is_active: boolean;
  activated_at: string;
  protected_until: string;
  synced_at?: number | null;
  updated_at?: string;
  created_at?: string;
}

export interface LocalUserWallet {
  localId?: number;
  id?: string;
  user_id: string;
  diamond_balance: number;
  lifetime_diamonds: number;
  diamonds_spent: number;
  organization_id?: string;
  synced_at?: number | null;
  updated_at?: string;
  created_at?: string;
}

export interface LocalUserNotification {
  localId?: number;
  id?: string;
  user_id: string;
  type: string;
  title: string;
  message: string;
  action_url?: string;
  read: boolean;
  expires_at?: string;
  organization_id?: string;
  synced_at?: number | null;
  updated_at?: string;
  created_at?: string;
}

// Cached trails for offline access (TTL 7 days)
export interface LocalCachedTrail {
  id: string; // Trail ID (rel-xxx or way-xxx)
  positionKey: string; // "lat,lng" arrondi pour regroupement
  name: string;
  type: 'hiking' | 'cycling' | 'walking' | 'running';
  distanceKm: number;
  estimatedDurationMin: number;
  difficulty: 'easy' | 'moderate' | 'hard';
  coordinates: [number, number][];
  startPoint: [number, number];
  endPoint: [number, number];
  isLoop: boolean;
  surface?: string;
  tags?: Record<string, string>;
  cachedAt: number;
  expiresAt: number; // TTL 7 jours
}

// Mapbox tiles for offline access
export interface LocalMapTile {
  tileKey: string; // "z/x/y" format
  styleId: string;
  blob: Blob;
  sizeBytes: number;
  cachedAt: number;
  expiresAt: number; // TTL 30 jours
}

// Offline map region downloaded by user
export interface LocalOfflineRegion {
  id: string;
  name: string;
  bounds: [number, number, number, number]; // [west, south, east, north]
  centerLat: number;
  centerLng: number;
  zoomMin: number;
  zoomMax: number;
  tilesCount: number;
  sizeMB: number;
  downloadedAt: number;
  expiresAt: number;
}

// GPS Track history for storing completed GPS tracking sessions
export interface LocalGPSTrack {
  localId?: number;
  id: string;
  user_id: string;
  goal_id: string;
  goal_title: string;
  challenge_id?: string;
  activity_type: 'walking' | 'running' | 'cycling' | 'hiking' | 'carpooling';
  started_at: number;
  ended_at: number;
  distance_km: number;
  duration_seconds: number;
  average_speed_kmh: number;
  estimated_calories: number;
  locations: Array<{
    latitude: number;
    longitude: number;
    timestamp: number;
    accuracy?: number;
    altitude?: number;
    speed?: number;
  }>;
  was_target_reached: boolean;
  was_validated: boolean;
  created_at: string;
}

// Avatar cache for storing Base64 Data URIs
export interface LocalAvatarCache {
  avatarId: string;
  dataUri: string;
  svg?: string; // Original SVG for regeneration if needed
  cachedAt: number;
  version: number;
}

// Lottie animation cache for premium avatars (reduces bundle size dramatically)
export interface LocalLottieCache {
  lottieId: string;
  json: string; // Stringified JSON (compressed)
  cachedAt: number;
  expiresAt: number; // TTL 7 days
  version: number;
  sizeBytes?: number;
}

export interface SyncQueueItem {
  id?: number;
  action: 'CREATE' | 'UPDATE' | 'DELETE';
  table: string;
  entityId?: string;
  data: any;
  created_at: number;
  retry_count: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error_message?: string;
  last_attempt_at?: number;
}

// Error reports queue for offline retry
export interface LocalErrorReportQueue {
  id?: number;
  fingerprint: string;
  reportData: any;
  created_at: number;
  retry_count: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  last_attempt_at?: number;
  error_message?: string;
}

export interface SyncMetadata {
  key: string;
  value: any;
}

// Commute tracking session for persistence/recovery
export interface LocalCommuteTrackingSession {
  id: string; // 'current_session' or unique id
  userId: string;
  routeName: string;
  mobilityType: string;
  locations: Array<{
    latitude: number;
    longitude: number;
    timestamp: number;
    accuracy?: number;
  }>;
  startTime: number;
  updatedAt: number;
  mode: 'record' | 'replay';
  referenceDistanceKm?: number;
  commuteLocalId?: string;
  sessionId?: string; // Supabase session ID
}

// Dexie database class
export class OfflineDatabase extends Dexie {
  userGoals!: Table<LocalUserGoal, number>;
  userProgression!: Table<LocalUserProgression, number>;
  userAccomplishments!: Table<LocalUserAccomplishment, number>;
  challengeParticipations!: Table<LocalChallengeParticipation, number>;
  userInventory!: Table<LocalUserInventory, number>;
  activeBoosts!: Table<LocalActiveBoost, number>;
  streakProtections!: Table<LocalStreakProtection, number>;
  userWallets!: Table<LocalUserWallet, number>;
  userNotifications!: Table<LocalUserNotification, number>;
  gpsTracksHistory!: Table<LocalGPSTrack, number>;
  avatarCache!: Table<LocalAvatarCache, string>;
  lottieCache!: Table<LocalLottieCache, string>;
  cachedTrails!: Table<LocalCachedTrail, string>;
  mapTiles!: Table<LocalMapTile, string>;
  offlineRegions!: Table<LocalOfflineRegion, string>;
  commuteTracking!: Table<LocalCommuteTrackingSession, string>;
  syncQueue!: Table<SyncQueueItem, number>;
  syncMetadata!: Table<SyncMetadata, string>;
  errorReportsQueue!: Table<LocalErrorReportQueue, number>;

  constructor() {
    super('KokenOfflineDB');
    
    this.version(1).stores({
      // User data with composite indexes for efficient queries
      userGoals: '++localId, id, user_id, goal_id, synced_at, updated_at, [user_id+goal_id], [user_id+is_selected]',
      userProgression: '++localId, id, user_id, synced_at, updated_at',
      userAccomplishments: '++localId, id, user_id, accomplishment_id, synced_at, updated_at, [user_id+accomplishment_id]',
      
      // Challenges
      challengeParticipations: '++localId, id, user_id, challenge_id, synced_at, updated_at, status, [user_id+challenge_id], [user_id+status]',
      
      // Gamification
      userInventory: '++localId, id, user_id, reward_id, synced_at, updated_at, status, [user_id+status]',
      activeBoosts: '++localId, id, user_id, synced_at, updated_at, status, [user_id+status]',
      streakProtections: '++localId, id, user_id, synced_at, updated_at, is_active, [user_id+is_active]',
      
      // Wallets (primarily read-only from server)
      userWallets: '++localId, id, user_id, synced_at, updated_at',
      
      // Notifications
      userNotifications: '++localId, id, user_id, synced_at, updated_at, read, [user_id+read]',
      
      // Sync management
      syncQueue: '++id, action, table, entityId, created_at, retry_count, status, [status+created_at]',
      syncMetadata: 'key'
    });

    // Version 2: Add GPS tracks history table
    this.version(2).stores({
      userGoals: '++localId, id, user_id, goal_id, synced_at, updated_at, [user_id+goal_id], [user_id+is_selected]',
      userProgression: '++localId, id, user_id, synced_at, updated_at',
      userAccomplishments: '++localId, id, user_id, accomplishment_id, synced_at, updated_at, [user_id+accomplishment_id]',
      challengeParticipations: '++localId, id, user_id, challenge_id, synced_at, updated_at, status, [user_id+challenge_id], [user_id+status]',
      userInventory: '++localId, id, user_id, reward_id, synced_at, updated_at, status, [user_id+status]',
      activeBoosts: '++localId, id, user_id, synced_at, updated_at, status, [user_id+status]',
      streakProtections: '++localId, id, user_id, synced_at, updated_at, is_active, [user_id+is_active]',
      userWallets: '++localId, id, user_id, synced_at, updated_at',
      userNotifications: '++localId, id, user_id, synced_at, updated_at, read, [user_id+read]',
      gpsTracksHistory: '++localId, id, user_id, goal_id, started_at, [user_id+goal_id]',
      syncQueue: '++id, action, table, entityId, created_at, retry_count, status, [status+created_at]',
      syncMetadata: 'key'
    });

    // Version 3: Add avatar cache table
    this.version(3).stores({
      userGoals: '++localId, id, user_id, goal_id, synced_at, updated_at, [user_id+goal_id], [user_id+is_selected]',
      userProgression: '++localId, id, user_id, synced_at, updated_at',
      userAccomplishments: '++localId, id, user_id, accomplishment_id, synced_at, updated_at, [user_id+accomplishment_id]',
      challengeParticipations: '++localId, id, user_id, challenge_id, synced_at, updated_at, status, [user_id+challenge_id], [user_id+status]',
      userInventory: '++localId, id, user_id, reward_id, synced_at, updated_at, status, [user_id+status]',
      activeBoosts: '++localId, id, user_id, synced_at, updated_at, status, [user_id+status]',
      streakProtections: '++localId, id, user_id, synced_at, updated_at, is_active, [user_id+is_active]',
      userWallets: '++localId, id, user_id, synced_at, updated_at',
      userNotifications: '++localId, id, user_id, synced_at, updated_at, read, [user_id+read]',
      gpsTracksHistory: '++localId, id, user_id, goal_id, started_at, [user_id+goal_id]',
      avatarCache: 'avatarId, cachedAt',
      syncQueue: '++id, action, table, entityId, created_at, retry_count, status, [status+created_at]',
      syncMetadata: 'key'
    });

    // Version 4: Add offline trails and map tiles cache
    this.version(4).stores({
      userGoals: '++localId, id, user_id, goal_id, synced_at, updated_at, [user_id+goal_id], [user_id+is_selected]',
      userProgression: '++localId, id, user_id, synced_at, updated_at',
      userAccomplishments: '++localId, id, user_id, accomplishment_id, synced_at, updated_at, [user_id+accomplishment_id]',
      challengeParticipations: '++localId, id, user_id, challenge_id, synced_at, updated_at, status, [user_id+challenge_id], [user_id+status]',
      userInventory: '++localId, id, user_id, reward_id, synced_at, updated_at, status, [user_id+status]',
      activeBoosts: '++localId, id, user_id, synced_at, updated_at, status, [user_id+status]',
      streakProtections: '++localId, id, user_id, synced_at, updated_at, is_active, [user_id+is_active]',
      userWallets: '++localId, id, user_id, synced_at, updated_at',
      userNotifications: '++localId, id, user_id, synced_at, updated_at, read, [user_id+read]',
      gpsTracksHistory: '++localId, id, user_id, goal_id, started_at, [user_id+goal_id]',
      avatarCache: 'avatarId, cachedAt',
      // Offline trails cache with position-based indexing
      cachedTrails: 'id, positionKey, type, cachedAt, expiresAt',
      // Mapbox tiles cache
      mapTiles: 'tileKey, styleId, cachedAt, expiresAt',
      // User-downloaded offline regions
      offlineRegions: 'id, downloadedAt, expiresAt',
      syncQueue: '++id, action, table, entityId, created_at, retry_count, status, [status+created_at]',
      syncMetadata: 'key'
    });

    // Version 5: Add error reports queue for offline retry
    this.version(5).stores({
      userGoals: '++localId, id, user_id, goal_id, synced_at, updated_at, [user_id+goal_id], [user_id+is_selected]',
      userProgression: '++localId, id, user_id, synced_at, updated_at',
      userAccomplishments: '++localId, id, user_id, accomplishment_id, synced_at, updated_at, [user_id+accomplishment_id]',
      challengeParticipations: '++localId, id, user_id, challenge_id, synced_at, updated_at, status, [user_id+challenge_id], [user_id+status]',
      userInventory: '++localId, id, user_id, reward_id, synced_at, updated_at, status, [user_id+status]',
      activeBoosts: '++localId, id, user_id, synced_at, updated_at, status, [user_id+status]',
      streakProtections: '++localId, id, user_id, synced_at, updated_at, is_active, [user_id+is_active]',
      userWallets: '++localId, id, user_id, synced_at, updated_at',
      userNotifications: '++localId, id, user_id, synced_at, updated_at, read, [user_id+read]',
      gpsTracksHistory: '++localId, id, user_id, goal_id, started_at, [user_id+goal_id]',
      avatarCache: 'avatarId, cachedAt',
      cachedTrails: 'id, positionKey, type, cachedAt, expiresAt',
      mapTiles: 'tileKey, styleId, cachedAt, expiresAt',
      offlineRegions: 'id, downloadedAt, expiresAt',
      syncQueue: '++id, action, table, entityId, created_at, retry_count, status, [status+created_at]',
      syncMetadata: 'key',
      // Error reports queue for offline retry with exponential backoff
      errorReportsQueue: '++id, fingerprint, status, created_at, [status+created_at]'
    });

    // Version 6: Add commute tracking session persistence
    this.version(6).stores({
      userGoals: '++localId, id, user_id, goal_id, synced_at, updated_at, [user_id+goal_id], [user_id+is_selected]',
      userProgression: '++localId, id, user_id, synced_at, updated_at',
      userAccomplishments: '++localId, id, user_id, accomplishment_id, synced_at, updated_at, [user_id+accomplishment_id]',
      challengeParticipations: '++localId, id, user_id, challenge_id, synced_at, updated_at, status, [user_id+challenge_id], [user_id+status]',
      userInventory: '++localId, id, user_id, reward_id, synced_at, updated_at, status, [user_id+status]',
      activeBoosts: '++localId, id, user_id, synced_at, updated_at, status, [user_id+status]',
      streakProtections: '++localId, id, user_id, synced_at, updated_at, is_active, [user_id+is_active]',
      userWallets: '++localId, id, user_id, synced_at, updated_at',
      userNotifications: '++localId, id, user_id, synced_at, updated_at, read, [user_id+read]',
      gpsTracksHistory: '++localId, id, user_id, goal_id, started_at, [user_id+goal_id]',
      avatarCache: 'avatarId, cachedAt',
      cachedTrails: 'id, positionKey, type, cachedAt, expiresAt',
      mapTiles: 'tileKey, styleId, cachedAt, expiresAt',
      offlineRegions: 'id, downloadedAt, expiresAt',
      // Commute tracking session for recovery after app kill
      commuteTracking: 'id, userId, updatedAt',
      syncQueue: '++id, action, table, entityId, created_at, retry_count, status, [status+created_at]',
      syncMetadata: 'key',
      errorReportsQueue: '++id, fingerprint, status, created_at, [status+created_at]'
    });

    // Version 7: Add Lottie animation cache for premium avatars
    this.version(7).stores({
      userGoals: '++localId, id, user_id, goal_id, synced_at, updated_at, [user_id+goal_id], [user_id+is_selected]',
      userProgression: '++localId, id, user_id, synced_at, updated_at',
      userAccomplishments: '++localId, id, user_id, accomplishment_id, synced_at, updated_at, [user_id+accomplishment_id]',
      challengeParticipations: '++localId, id, user_id, challenge_id, synced_at, updated_at, status, [user_id+challenge_id], [user_id+status]',
      userInventory: '++localId, id, user_id, reward_id, synced_at, updated_at, status, [user_id+status]',
      activeBoosts: '++localId, id, user_id, synced_at, updated_at, status, [user_id+status]',
      streakProtections: '++localId, id, user_id, synced_at, updated_at, is_active, [user_id+is_active]',
      userWallets: '++localId, id, user_id, synced_at, updated_at',
      userNotifications: '++localId, id, user_id, synced_at, updated_at, read, [user_id+read]',
      gpsTracksHistory: '++localId, id, user_id, goal_id, started_at, [user_id+goal_id]',
      avatarCache: 'avatarId, cachedAt',
      // Lottie cache for premium animated avatars - reduces bundle size
      lottieCache: 'lottieId, cachedAt, expiresAt',
      cachedTrails: 'id, positionKey, type, cachedAt, expiresAt',
      mapTiles: 'tileKey, styleId, cachedAt, expiresAt',
      offlineRegions: 'id, downloadedAt, expiresAt',
      commuteTracking: 'id, userId, updatedAt',
      syncQueue: '++id, action, table, entityId, created_at, retry_count, status, [status+created_at]',
      syncMetadata: 'key',
      errorReportsQueue: '++id, fingerprint, status, created_at, [status+created_at]'
    });
  }
}

// Create singleton instance
export const offlineDb = new OfflineDatabase();

// Helper function to get last sync timestamp
export async function getLastSyncTimestamp(key: string = 'lastFullSync'): Promise<number | null> {
  const metadata = await offlineDb.syncMetadata.get(key);
  return metadata?.value || null;
}

// Helper function to set last sync timestamp
export async function setLastSyncTimestamp(timestamp: number, key: string = 'lastFullSync'): Promise<void> {
  await offlineDb.syncMetadata.put({ key, value: timestamp });
}

// Helper function to clear all data (useful for logout)
export async function clearAllOfflineData(): Promise<void> {
  await Promise.all([
    offlineDb.userGoals.clear(),
    offlineDb.userProgression.clear(),
    offlineDb.userAccomplishments.clear(),
    offlineDb.challengeParticipations.clear(),
    offlineDb.userInventory.clear(),
    offlineDb.activeBoosts.clear(),
    offlineDb.streakProtections.clear(),
    offlineDb.userWallets.clear(),
    offlineDb.userNotifications.clear(),
    offlineDb.syncQueue.clear(),
    offlineDb.syncMetadata.clear()
    // Note: avatarCache is NOT cleared on logout (shared across users)
  ]);
}

// Helper function to clear user-specific data
export async function clearUserData(userId: string): Promise<void> {
  await Promise.all([
    offlineDb.userGoals.where('user_id').equals(userId).delete(),
    offlineDb.userProgression.where('user_id').equals(userId).delete(),
    offlineDb.userAccomplishments.where('user_id').equals(userId).delete(),
    offlineDb.challengeParticipations.where('user_id').equals(userId).delete(),
    offlineDb.userInventory.where('user_id').equals(userId).delete(),
    offlineDb.activeBoosts.where('user_id').equals(userId).delete(),
    offlineDb.streakProtections.where('user_id').equals(userId).delete(),
    offlineDb.userWallets.where('user_id').equals(userId).delete(),
    offlineDb.userNotifications.where('user_id').equals(userId).delete()
  ]);
}

/**
 * ✅ FIX: Enforce a maximum size limit on the goals cache to prevent unbounded growth
 * Prunes oldest entries when limit is exceeded
 */
export async function enforceGoalsCacheLimit(userId: string): Promise<void> {
  const MAX_GOALS_PER_USER = 200;
  
  try {
    const userGoals = await offlineDb.userGoals
      .where('user_id')
      .equals(userId)
      .toArray();
      
    if (userGoals.length > MAX_GOALS_PER_USER) {
      console.warn('[CACHE] Goal cache overflow, pruning oldest entries', { 
        currentCount: userGoals.length,
        limit: MAX_GOALS_PER_USER
      });
      
      // Sort by synced_at ASC (oldest first)
      const sortedByAge = userGoals.sort((a, b) => 
        (a.synced_at || 0) - (b.synced_at || 0)
      );
      
      const toDelete = sortedByAge.slice(0, userGoals.length - MAX_GOALS_PER_USER);
      for (const goal of toDelete) {
        if (goal.localId) {
          await offlineDb.userGoals.delete(goal.localId);
        }
      }
      
      console.log('[CACHE] Pruned goal cache', { deleted: toDelete.length });
    }
  } catch (e) {
    console.error('[CACHE] Failed to enforce goal cache limit', e);
  }
}

/**
 * ✅ FIX: Emergency cleanup for corrupted cache (>1000 entries)
 * Called at app boot to recover from cache corruption
 */
export async function cleanupCorruptedGoalsCache(): Promise<void> {
  try {
    const count = await offlineDb.userGoals.count();
    if (count > 1000) {
      console.warn('[CACHE CLEANUP] Corrupted goals cache detected, clearing...', { count });
      await offlineDb.userGoals.clear();
      console.log('[CACHE CLEANUP] Goals cache cleared');
    }
  } catch (e) {
    console.error('[CACHE CLEANUP] Failed', e);
  }
}
